import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Phone, Package, Trash2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { toast } from "sonner";

const MAIL = { sent: "bg-emerald-50 text-emerald-700", failed: "bg-red-50 text-red-700", skipped: "bg-slate-100 text-slate-500", disabled: "bg-slate-100 text-slate-500" };
function MailStatus({ label, s, testid }) {
  if (!s) return null;
  const txt = { sent: "delivered", failed: "failed — queued for retry", skipped: "skipped", disabled: "off" }[s.status] || s.status;
  return <span title={s.error || ""} className={`text-[11px] px-1.5 py-0.5 rounded font-semibold ${MAIL[s.status] || "bg-slate-100"}`} data-testid={testid}>{label}: {txt}</span>;
}

const STATUS = { new: "New", in_progress: "In Progress", handled: "Handled" };
const color = (s) => ({ new: "bg-accent text-steel", in_progress: "bg-blue-600", handled: "bg-emerald-600" }[s] || "bg-slate-500");

export default function EnquiriesTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [picked, setPicked] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const load = () => api.get("/admin/enquiries").then((r) => { setItems(r.data); setPicked((p) => p.filter((id) => r.data.some((e) => e.id === id))); });
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/enquiries/${id}`, { status }); toast.success("Enquiry updated"); load(); }
    catch { toast.error("Could not update enquiry"); }
  };

  const shown = items.filter((i) => filter === "all" || (i.status || "new") === filter);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const doDelete = async () => {
    const ids = confirm;
    try {
      if (ids.length === 1) await api.delete(`/admin/enquiries/${ids[0]}`);
      else await api.post("/admin/enquiries/bulk-delete", { ids });
      toast.success(`${ids.length} enquir${ids.length === 1 ? "y" : "ies"} deleted`);
      setConfirm(null); load();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div>
      <h1 className="text-3xl font-extrabold mb-1">Enquiries</h1>
      <p className="text-muted-foreground mb-6">Customer product enquiries and contact-form messages.</p>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="w-56">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="enquiry-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="handled">Handled</SelectItem>
          </SelectContent>
        </Select>
        </div>
        {shown.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => setPicked(picked.length === shown.length ? [] : shown.map((e) => e.id))} data-testid="enquiry-select-all">
            {picked.length === shown.length ? "Clear selection" : `Select all (${shown.length})`}
          </Button>
        )}
        {picked.length > 0 && (
          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => setConfirm(picked)} data-testid="enquiry-bulk-delete">
            <Trash2 size={14} className="mr-1" /> Delete {picked.length} selected
          </Button>
        )}
      </div>
      <ConfirmDelete open={!!confirm} title={`Delete ${confirm?.length || 0} enquir${confirm?.length === 1 ? "y" : "ies"}?`}
        description="This permanently removes the enquiry and its message. This can't be undone." onConfirm={doDelete} onCancel={() => setConfirm(null)} />

      <div className="space-y-3">
        {shown.length === 0 && <div className="rounded-xl border bg-white p-8 text-center text-muted-foreground">No enquiries.</div>}
        {shown.map((e) => (
          <div key={e.id} data-testid={`enquiry-${e.id}`} className="rounded-xl border bg-white p-5">
            <div className="flex flex-wrap items-start gap-4">
              <Checkbox checked={picked.includes(e.id)} onCheckedChange={() => toggle(e.id)} className="mt-1" data-testid={`enquiry-select-${e.id}`} />
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{e.name}</span>
                  <Badge className={`${color(e.status || "new")} hover:${color(e.status || "new")}`}>{STATUS[e.status || "new"]}</Badge>
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold" data-testid={`enquiry-received-${e.id}`}>Enquiry received</span>
                  <MailStatus label="Admin email" s={e.admin_email} testid={`enquiry-admin-email-${e.id}`} />
                  <MailStatus label="Customer ack" s={e.ack_email} testid={`enquiry-ack-email-${e.id}`} />
                  {!e.admin_email && !e.ack_email && <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" data-testid={`enquiry-email-untracked-${e.id}`}>Email: not tracked (older enquiry)</span>}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1"><Mail size={13} /> {e.email}</span>
                  {e.phone && <span className="flex items-center gap-1"><Phone size={13} /> {e.phone}</span>}
                </div>
                {e.message && <p className="mt-2 text-sm bg-slate-50 border rounded-lg p-3">{e.message}</p>}
                {(e.product_name || e.sku) && (
                  <a href={e.product_id ? `/product/${e.product_id}` : "#"} target="_blank" rel="noreferrer"
                    data-testid={`enquiry-product-${e.id}`}
                    className="mt-2 flex items-center gap-3 rounded-lg border bg-slate-50 p-2 hover:border-accent transition-colors">
                    {e.product_image
                      ? <img src={e.product_image} alt={e.product_name} className="w-12 h-12 rounded object-cover bg-white border" onError={(ev) => { ev.currentTarget.style.display = "none"; }} />
                      : <div className="w-12 h-12 rounded bg-white border grid place-items-center text-slate-300"><Package size={20} /></div>}
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">Enquiring about</div>
                      <div className="font-semibold truncate">{e.product_name || e.sku}</div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-mono">{e.sku}</span>
                        {e.product_price > 0
                          ? <span className="font-semibold text-accent">${e.product_price.toFixed(2)}</span>
                          : <span className="italic">POA / Enquire for price</span>}
                      </div>
                    </div>
                  </a>
                )}
                <div className="mt-1 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString("en-NZ", { timeZone: "Pacific/Auckland" })}</div>
              </div>
              <div className="w-44">
                <Select value={e.status || "new"} onValueChange={(v) => setStatus(e.id, v)}>
                  <SelectTrigger data-testid={`enquiry-status-${e.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="handled">Handled</SelectItem>
                  </SelectContent>
                </Select>
                {e.email && (
                  <a href={`mailto:${e.email}`} className="inline-block mt-2 text-accent hover:underline text-sm">Reply by email →</a>
                )}
                <button onClick={() => setConfirm([e.id])} data-testid={`enquiry-delete-${e.id}`}
                  className="mt-2 flex items-center gap-1 text-sm text-slate-500 hover:text-red-600"><Trash2 size={14} /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
